// Send a request — letter-first composer per Design Pack §4.4.
//
// The letter is the dominant surface (≥80 chars to enable Send). Side panel
// (or above on mobile) carries dates, party size, art form, and the optional
// stipend toggle. Auto-saves draft every 5s after the first non-empty edit;
// the first save is a POST to create a draft row, every subsequent save is a
// PATCH onto the same id.
//
// Deep-link prefill: a tour-route match card may pass `?from=YYYY-MM-DD&
// to=YYYY-MM-DD&route=<id>&city=<lower>` to seed dates + a soft opener.

import {
  Accordion,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  CopyButton,
  Grid,
  Group,
  Image,
  Loader,
  NumberInput,
  Paper,
  Select,
  Skeleton,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ART_FORMS } from '@wwaave/shared/vocab';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { AppShell } from '../../components/app-shell/AppShell';
import { EmptyState } from '../../components/ui';
import { fetchJson } from '../../lib/api';
import { csrfHeaders, useAuth } from '../../lib/auth-context';

// Search-param contract: every field optional so the route still works when
// linked from a venue page without prefill.
const requestSearchSchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  route: z.string().min(1).optional(),
  city: z.string().min(1).optional(),
});

export const Route = createFileRoute('/request/$venueId')({
  component: RequestComposer,
  validateSearch: requestSearchSchema,
});

const MIN_LETTER_CHARS = 80;
const MAX_LETTER_CHARS = 4000;
const AUTOSAVE_INTERVAL_MS = 5000;

type DraftFields = {
  startDate: Date | null;
  endDate: Date | null;
  partySize: number;
  artForm: string;
  letter: string;
  stipendOffered: boolean;
};

function ymdToDate(s: string | undefined): Date | null {
  if (s === undefined || s === '') return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isFinite(d.valueOf()) ? d : null;
}

function titleCase(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

const MONTH_FMT = new Intl.DateTimeFormat('en-AU', { month: 'long' });
const DAY_FMT = new Intl.DateTimeFormat('en-AU', { day: 'numeric' });

function dayOfMonth(d: Date): number {
  return d.getDate();
}

/**
 * Render a window like "early July" / "mid August" / "late November" when
 * within a single month, or "7–14 July" when it's a short same-month range,
 * or "7 July – 3 August" when it straddles months.
 */
function describeWindow(from: Date, to: Date): string {
  const sameMonth =
    from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth();
  if (sameMonth) {
    const fromDay = dayOfMonth(from);
    const toDay = dayOfMonth(to);
    const month = MONTH_FMT.format(from);
    // Single day, or very tight window — call it like "7 July".
    if (toDay - fromDay <= 1) {
      return `${DAY_FMT.format(from)} ${month}`;
    }
    // Within a week — name the chunk of the month.
    if (toDay - fromDay <= 7) {
      let chunk: string;
      if (fromDay <= 10) chunk = 'early';
      else if (fromDay <= 20) chunk = 'mid';
      else chunk = 'late';
      return `${chunk} ${month}`;
    }
    return `${DAY_FMT.format(from)}–${DAY_FMT.format(to)} ${month}`;
  }
  return `${DAY_FMT.format(from)} ${MONTH_FMT.format(from)} – ${DAY_FMT.format(to)} ${MONTH_FMT.format(to)}`;
}

function makeSoftOpener(opts: { window: string; city: string | undefined }): string {
  const cityLine =
    opts.city !== undefined && opts.city.length > 0
      ? ` I'd love to play in ${titleCase(opts.city)} on this leg.`
      : '';
  return `Hi there. I'm planning a tour through your area in ${opts.window}.${cityLine}`;
}

type ConflictDTO = {
  exchanges: Array<{ startDate: string; endDate: string; venueName: string | null }>;
  blocked: Array<{ startDate: string; endDate: string }>;
};

type VenueSummaryDTO = {
  id: string;
  name: string;
  hostName: string | null;
  slug: string;
  city: string | null;
  country: string | null;
  heroUrl: string | null;
  artForms: Array<{ slug: string; name: string }>;
  stipendOffered: boolean;
  sleepOffered: boolean;
  capacity: number | null;
};

function VenueSnapshotCard({ venueId }: { venueId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['venue-summary', venueId],
    queryFn: () =>
      fetchJson<VenueSummaryDTO>(
        `/api/v/venues/by-id/${encodeURIComponent(venueId)}/summary`,
      ),
  });

  if (isLoading) {
    return (
      <Card withBorder shadow="sm" radius="md" p="sm">
        <Group gap="sm" wrap="nowrap">
          <Skeleton w={72} h={72} radius="md" />
          <Stack gap={4} style={{ flex: 1 }}>
            <Skeleton h={16} w="80%" />
            <Skeleton h={12} w="50%" />
            <Skeleton h={12} w="40%" />
          </Stack>
        </Group>
      </Card>
    );
  }

  if (!data) return null;

  const location = [data.city, data.country].filter(Boolean).join(', ');

  return (
    <Card withBorder shadow="sm" radius="md" p="sm">
      <Group gap="sm" wrap="nowrap" align="flex-start">
        {data.heroUrl ? (
          <Image
            src={data.heroUrl}
            alt={data.name}
            w={72}
            h={72}
            radius="md"
            fit="cover"
          />
        ) : (
          <Box
            w={72}
            h={72}
            style={{
              borderRadius: 'var(--mantine-radius-md)',
              background: 'var(--wwaave-accent-gradient)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text size="lg" fw={700} style={{ opacity: 0.35, letterSpacing: 1, color: 'light-dark(var(--mantine-color-brand-7), var(--mantine-color-brand-3))' }}>
              {data.name.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('')}
            </Text>
          </Box>
        )}
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Link
            to="/venues/$slug"
            params={{ slug: data.slug }}
            style={{ fontWeight: 600, fontSize: 'var(--mantine-font-size-sm)', color: 'var(--mantine-color-anchor)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {data.name}
          </Link>
          {data.hostName && (
            // §6 — "You're writing to" should name the contact, not just the venue.
            <Text size="xs" c="dimmed" lineClamp={1}>
              Hosted by {data.hostName}
            </Text>
          )}
          {location.length > 0 && (
            <Text size="xs" c="dimmed" lineClamp={1}>{location}</Text>
          )}
          {data.artForms.length > 0 && (
            <Group gap={4} wrap="wrap">
              {data.artForms.slice(0, 4).map((af) => (
                <Badge key={af.slug} size="xs" variant="light" color="gray">
                  {af.name}
                </Badge>
              ))}
              {data.artForms.length > 4 && (
                <Text size="xs" c="dimmed">+{data.artForms.length - 4}</Text>
              )}
            </Group>
          )}
          <Group gap="xs" mt={2}>
            {data.stipendOffered && (
              <Text size="xs" c="tide.7">Stipend offered</Text>
            )}
            {data.sleepOffered && (
              <Text size="xs" c="blue.7">Sleep available</Text>
            )}
            {data.capacity !== null && (
              <Text size="xs" c="dimmed">Cap. {data.capacity}</Text>
            )}
          </Group>
        </Stack>
      </Group>
    </Card>
  );
}

function VenueSnapshotCollapsed({ venueId }: { venueId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['venue-summary', venueId],
    queryFn: () =>
      fetchJson<VenueSummaryDTO>(
        `/api/v/venues/by-id/${encodeURIComponent(venueId)}/summary`,
      ),
  });

  if (isLoading) {
    return <Skeleton h={40} radius="md" />;
  }

  if (!data) return null;

  const location = [data.city, data.country].filter(Boolean).join(', ');

  return (
    <Accordion variant="separated" radius="md" chevronPosition="right">
      <Accordion.Item value="venue-context">
        <Accordion.Control>
          <Group gap="xs">
            <Text size="sm" fw={600}>{data.name}</Text>
            {location.length > 0 && (
              <Text size="xs" c="dimmed">{location}</Text>
            )}
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <VenueSnapshotCard venueId={venueId} />
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

// FR-085: the composer autosaves to `POST /api/exchanges`, which 403s for any
// non-artist ("Only artists can send requests."). A host-only or hat-switched
// user clicking "Send a request" from a venue page used to land in the full
// composer, write a whole letter, and only then hit a generic "couldn't save"
// error with their draft lost. Gate in-page (like /billing does for non-hosts):
// resolve capabilities first, then either explain the situation or mount the
// composer. Keeping the composer itself in a child means its ~dozen hooks never
// run for a non-artist.
function RequestComposer() {
  const { capabilities, isLoading } = useAuth();

  if (isLoading || capabilities === null) {
    return (
      <AppShell tone="content">
        <Center py={80}>
          <Loader />
        </Center>
      </AppShell>
    );
  }

  if (!capabilities.isArtist) {
    return (
      <AppShell tone="form">
        <EmptyState
          title="Requests are sent from an artist profile"
          body="Only artists send stay requests. If you're here to host, you manage venues and incoming requests from your host dashboard. To perform, add an artist profile from Settings → Account and you can start reaching out to venues."
          action={
            <Button component={Link} to="/settings/account" variant="light">
              Go to account settings
            </Button>
          }
        />
      </AppShell>
    );
  }

  return <RequestComposerBody />;
}

function RequestComposerBody() {
  const { venueId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();

  const prefillStart = useMemo(() => ymdToDate(search.from), [search.from]);
  const prefillEnd = useMemo(() => ymdToDate(search.to), [search.to]);

  const [fields, setFields] = useState<DraftFields>(() => {
    const opener =
      prefillStart !== null && prefillEnd !== null
        ? makeSoftOpener({
            window: describeWindow(prefillStart, prefillEnd),
            city: search.city,
          })
        : '';
    return {
      startDate: prefillStart,
      endDate: prefillEnd,
      partySize: 2,
      artForm: '',
      letter: search.route !== undefined ? opener : '',
      stipendOffered: false,
    };
  });
  const [draftId, setDraftId] = useState<string | null>(null);
  const [savingState, setSavingState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const lastSavedSnapshotRef = useRef<string>('');
  const tourRouteId = search.route;

  const artFormOptions = useMemo(
    () => ART_FORMS.map((a) => ({ value: a.slug, label: a.name })),
    [],
  );

  // Soft overlap check — warn (never block) when the chosen window collides
  // with a confirmed stay or a day the artist has marked away. Endpoint is
  // artist-only; for anyone else it 4xxs and we quietly show nothing.
  const conflictFrom = fields.startDate !== null ? dateOnly(fields.startDate) : null;
  const conflictTo = fields.endDate !== null ? dateOnly(fields.endDate) : null;
  const conflictQuery = useQuery({
    queryKey: ['calendar-conflicts', conflictFrom, conflictTo],
    enabled: conflictFrom !== null && conflictTo !== null,
    staleTime: 30_000,
    queryFn: async (): Promise<ConflictDTO> => {
      const res = await fetch(
        `/api/artist-calendar/me/calendar/conflicts?from=${conflictFrom!}&to=${conflictTo!}`,
        { credentials: 'include' },
      );
      if (!res.ok) return { exchanges: [], blocked: [] };
      return (await res.json()) as ConflictDTO;
    },
  });
  const conflicts = conflictQuery.data;
  const hasConflict =
    conflicts !== undefined &&
    (conflicts.exchanges.length > 0 || conflicts.blocked.length > 0);

  const letterCount = fields.letter.length;
  const letterMeetsMin = letterCount >= MIN_LETTER_CHARS;
  const datesSet = fields.startDate !== null && fields.endDate !== null;
  const artFormSet = fields.artForm.length > 0;
  const sendDisabled =
    !letterMeetsMin || !datesSet || !artFormSet || letterCount > MAX_LETTER_CHARS || submitting;

  // Auto-save loop. Fires every 5s if the snapshot has changed since the last
  // save AND there's something worth saving (any field touched).
  useEffect(() => {
    const interval = setInterval(() => {
      const snapshot = JSON.stringify(fields);
      if (snapshot === lastSavedSnapshotRef.current) return;
      // Don't open a draft on the server until the artist has typed something.
      if (
        fields.letter.trim().length === 0 &&
        fields.startDate === null &&
        fields.endDate === null
      ) {
        return;
      }
      void saveDraft(snapshot);
    }, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, draftId, venueId, tourRouteId]);

  async function saveDraft(snapshot: string): Promise<void> {
    setSavingState('saving');
    try {
      const body = serialiseDraft(fields, venueId, tourRouteId);
      let res: Response;
      if (draftId === null) {
        // No trailing slash — `/api/exchanges/` falls through Hono's route match
        // to the SPA fallback. CSRF header is required: the global /api/* gate
        // 403s any tokenless mutation (this was the "requests not sending" bug).
        res = await fetch('/api/exchanges', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json', ...csrfHeaders() },
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`/api/exchanges/${encodeURIComponent(draftId)}`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'content-type': 'application/json', ...csrfHeaders() },
          body: JSON.stringify(stripVenueId(body)),
        });
      }
      if (!res.ok) {
        setSavingState('error');
        return;
      }
      const json = (await res.json()) as { ok: boolean; id?: string };
      if (json.ok && draftId === null && typeof json.id === 'string') {
        setDraftId(json.id);
      }
      lastSavedSnapshotRef.current = snapshot;
      setSavingState('saved');
    } catch {
      setSavingState('error');
    }
  }

  async function handleSend(): Promise<void> {
    if (sendDisabled) return;
    setSubmitting(true);
    setSubmitError(null);

    // Make sure the latest typed values are persisted before we ask the server
    // to transition state — otherwise the server's state-machine context reads
    // stale fields.
    const snapshot = JSON.stringify(fields);
    if (snapshot !== lastSavedSnapshotRef.current) {
      await saveDraft(snapshot);
    }
    if (draftId === null) {
      // Save above failed; surface a clear error rather than blocking forever.
      setSubmitError("We couldn't save your draft. Try again in a moment.");
      setSubmitting(false);
      return;
    }

    try {
      const body = {
        exchange_id: draftId,
        opening_letter: fields.letter,
        party_size: fields.partySize,
        start_date: dateOnly(fields.startDate!),
        end_date: dateOnly(fields.endDate!),
        art_form: fields.artForm,
        stipend_offered: fields.stipendOffered,
      };
      const res = await fetch(`/api/exchanges/${encodeURIComponent(draftId)}/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify(body),
      });
      const json = (await res.json()) as {
        ok: boolean;
        error?: { message?: string };
      };
      if (!res.ok || !json.ok) {
        setSubmitError(json.error?.message ?? "Couldn't send your request. Try again.");
        setSubmitting(false);
        return;
      }
      notifications.show({
        title: 'Request sent',
        message: "We'll let you know as soon as the host replies.",
      });
      void navigate({
        to: '/inbox/$threadId',
        params: { threadId: `thread_${draftId}` },
      });
    } catch {
      setSubmitError("Connection dropped. Your draft is saved. Try again when you're back online.");
      setSubmitting(false);
    }
  }

  return (
    <AppShell tone="content">
      <Stack gap="lg">
        <Box>
          <Title order={2}>Write a letter to the host</Title>
          <Text c="dimmed" size="sm">
            Write to the host like you would to a friend running the room. What you'd bring,
            what you need, why this place caught you.
          </Text>
        </Box>

        <Box hiddenFrom="lg">
          <VenueSnapshotCollapsed venueId={venueId} />
        </Box>

        <Grid gap="lg">
          <Grid.Col span={{ base: 12, lg: 3 }} visibleFrom="lg">
            <Box style={{ position: 'sticky', top: 80 }}>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={8} style={{ letterSpacing: '0.04em' }}>
                You're writing to
              </Text>
              <VenueSnapshotCard venueId={venueId} />
            </Box>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 7, lg: 5 }}>
            <Paper withBorder p="md" radius="md">
              <Stack gap="sm">
                <TextInput
                  label="Subject (a quick note for yourself)"
                  description="Not visible to the host."
                  placeholder="Brisbane → Gold Coast tour, May"
                  disabled
                  value=""
                  onChange={() => undefined}
                />
                <Textarea
                  label="Your letter"
                  description={`At least ${MIN_LETTER_CHARS} characters. Tell them about you, your art, and why this venue caught your eye.`}
                  autosize
                  minRows={12}
                  maxLength={MAX_LETTER_CHARS}
                  value={fields.letter}
                  onChange={(e) => setFields({ ...fields, letter: e.currentTarget.value })}
                />
                <Group justify="space-between">
                  <Text size="xs" c={letterMeetsMin ? 'dimmed' : 'red'}>
                    {letterCount} / {MAX_LETTER_CHARS} characters
                    {!letterMeetsMin
                      ? `, ${MIN_LETTER_CHARS - letterCount} more to go`
                      : ''}
                  </Text>
                </Group>
                {letterCount < 40 ? <OpenerExample /> : null}
                <Text size="xs" c="dimmed" data-testid="autosave-status" mt="sm">
                  {savingState === 'saving'
                    ? 'Saving draft…'
                    : savingState === 'saved'
                      ? 'Draft saved'
                      : savingState === 'error'
                        ? "Couldn't save just now. We'll try again"
                        : ' '}
                </Text>
                <Group justify="flex-end" mt="xs">
                  <Button
                    variant="default"
                    onClick={() => void saveDraft(JSON.stringify(fields))}
                    disabled={savingState === 'saving'}
                  >
                    Save draft
                  </Button>
                  <Button
                    onClick={handleSend}
                    disabled={sendDisabled}
                    loading={submitting}
                    variant="gradient"
                    gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                  >
                    Send request
                  </Button>
                </Group>
                {submitError !== null ? (
                  <Alert color="red" variant="light">
                    {submitError}
                  </Alert>
                ) : null}
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 5, lg: 4 }}>
            <Paper withBorder p="md" radius="md">
              <Stack gap="md">
                <Title order={4}>Your request</Title>
                <DatePickerInput
                  type="range"
                  label="Dates"
                  description="When would you like to be there?"
                  placeholder="Pick dates"
                  value={[fields.startDate, fields.endDate]}
                  onChange={(range) => {
                    const [s, e] = range;
                    const sDate = s !== null ? new Date(s) : null;
                    const eDate = e !== null ? new Date(e) : null;
                    setFields({ ...fields, startDate: sDate, endDate: eDate });
                  }}
                  minDate={new Date()}
                />
                {hasConflict && conflicts !== undefined ? (
                  <Alert color="yellow" variant="light" title="Heads up, a clash on these dates">
                    <Stack gap={4}>
                      {conflicts.exchanges.map((ex) => (
                        <Text key={`${ex.startDate}-${ex.endDate}`} size="sm">
                          You have a confirmed stay
                          {ex.venueName !== null ? ` at ${ex.venueName}` : ''} (
                          {describeWindow(
                            new Date(`${ex.startDate}T00:00:00`),
                            new Date(`${ex.endDate}T00:00:00`),
                          )}
                          ).
                        </Text>
                      ))}
                      {conflicts.blocked.length > 0 ? (
                        <Text size="sm">You&apos;ve marked yourself away on some of these days.</Text>
                      ) : null}
                      <Text size="xs" c="dimmed">
                        You can still send this request. Just a nudge in case it slipped your mind.
                      </Text>
                    </Stack>
                  </Alert>
                ) : null}
                <NumberInput
                  label="Party size"
                  description="How many of you will travel?"
                  min={1}
                  max={50}
                  value={fields.partySize}
                  onChange={(v) =>
                    setFields({ ...fields, partySize: typeof v === 'number' ? v : 1 })
                  }
                />
                <Select
                  label="Art form"
                  description="The host has set which art forms they welcome."
                  placeholder="Pick one"
                  data={artFormOptions}
                  value={fields.artForm}
                  onChange={(v) => setFields({ ...fields, artForm: v ?? '' })}
                  searchable
                />
                <Paper p="sm" radius="md" bg="light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))">
                  <Checkbox
                    label="I'd appreciate a travel stipend"
                    checked={fields.stipendOffered}
                    onChange={(e) => setFields({ ...fields, stipendOffered: e.currentTarget.checked })}
                  />
                </Paper>
                <Text size="xs" c="dimmed">
                  Notes you've added to your profile (dietary, sleeping, accessibility) are shared
                  automatically once the host confirms.
                </Text>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>
    </AppShell>
  );
}

// Single-example opener panel. Collapsed by default — Maya facing a blank
// textarea on a 360px phone is exactly the moment we lose her, and a worked
// example beats prescriptive guidance. The example models specific-not-
// generic voice (named host, named city window, named room characteristic,
// concrete ask). Disappears once the artist has a real sentence going (≥40
// chars) so it doesn't crowd the composer mid-flow. Copy-able, not
// auto-fill — borrowing voice is the point; copy-pasting the example is not.
const OPENER_EXAMPLE = `Hi Sarah, I'm Maya, a folk-leaning singer-songwriter on a SE QLD run in early June. I've heard your room is quiet enough that fingerstyle holds up. If you're open to a midweek night, I'd love to play.`;

function OpenerExample() {
  return (
    <Accordion variant="separated" radius="md" chevronPosition="right">
      <Accordion.Item value="opener-example">
        <Accordion.Control>
          <Text size="sm" fw={600}>
            What hosts read first
          </Text>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              One opener that works. Borrow the shape, not the words. A named host, a
              window of dates, one specific thing about the room.
            </Text>
            <Paper bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))" p="sm" radius="sm" withBorder>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {OPENER_EXAMPLE}
              </Text>
            </Paper>
            <Group justify="flex-end">
              <CopyButton value={OPENER_EXAMPLE}>
                {({ copied, copy }) => (
                  <Button size="xs" variant="subtle" onClick={copy}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

function serialiseDraft(
  fields: DraftFields,
  venueId: string,
  tourRouteId: string | undefined,
): Record<string, unknown> {
  return {
    venue_id: venueId,
    start_date: fields.startDate !== null ? dateOnly(fields.startDate) : '0000-00-00',
    end_date: fields.endDate !== null ? dateOnly(fields.endDate) : '0000-00-00',
    party_size: fields.partySize,
    art_form: fields.artForm.length > 0 ? fields.artForm : ART_FORMS[0]!.slug,
    opening_letter: fields.letter,
    stipend_offered: fields.stipendOffered,
    ...(tourRouteId !== undefined && tourRouteId.length > 0
      ? { tour_route_id: tourRouteId }
      : {}),
  };
}

function stripVenueId(body: Record<string, unknown>): Record<string, unknown> {
  // PATCH /:id reuses the venue from the draft; we can't change it after create.
   
  const { venue_id: _vid, ...rest } = body;
  return rest;
}

function dateOnly(d: Date): string {
  // YYYY-MM-DD in local tz of the artist. The PRD treats the date string as
  // a calendar day in the venue's timezone for completion windows; the server
  // applies the venue tz when needed.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
